import * as Sentry from '@sentry/react';

import { AuthService } from '@/entities/auth/auth-service';

// ============================================================================
// Generic SSE Connection Types & Functions
// ============================================================================

export interface SSEEvent<TEventType = string, TData = unknown> {
  type: TEventType;
  data: TData;
  id?: string;
}

export interface SSEOptions<TEventType = string, TData = unknown> {
  onMessage?: (event: SSEEvent<TEventType, TData>) => void;
  onError?: (error: Error) => void;
  onDone?: () => void;
}

export interface SSEController {
  abort: () => void;
  isConnected: () => boolean;
}

export interface SSERequestConfig {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
}

export interface AuthenticatedSSERequestConfig extends SSERequestConfig {
  maxAuthRetryAttempts?: number;
}

const MAX_AUTH_RETRY_ATTEMPTS = 3;
let refreshPromise: Promise<void> | undefined = undefined;

class SSEHttpError extends Error {
  status: number;
  statusText: string;

  constructor(status: number, statusText: string) {
    super(`SSE connection failed: ${status} ${statusText}`);
    this.name = 'SSEHttpError';
    this.status = status;
    this.statusText = statusText;
  }
}

const isSSEHttpError = (error: unknown): error is SSEHttpError => {
  return (
    error instanceof Error &&
    error.name === 'SSEHttpError' &&
    'status' in error &&
    typeof (error as SSEHttpError).status === 'number'
  );
};

const refreshAccessToken = async () => {
  if (refreshPromise == null) {
    refreshPromise = AuthService.refreshToken().finally(() => {
      refreshPromise = undefined;
    });
  }

  await refreshPromise;
};

const handleAuthFailure = () => {
  AuthService.removeTokens();
  window.location.href = '/login';
};

/**
 * 일반적인 SSE 연결을 생성하는 추상화된 함수입니다.
 * 응답 데이터는 JSON 형태로 자동 파싱됩니다.
 *
 * @param config - SSE 요청 설정
 * @param options - SSE 이벤트 핸들러 옵션
 * @returns 연결을 제어할 수 있는 컨트롤러 객체
 */
export function createSSEConnection<TEventType = string, TData = unknown>(
  config: SSERequestConfig,
  options: SSEOptions<TEventType, TData> = {},
): SSEController {
  const { onMessage, onError, onDone } = options;
  const abortController = config.signal ? undefined : new AbortController();
  const signal = config.signal ?? abortController?.signal;
  let isConnected = false;

  const connectSSE = async () => {
    try {
      isConnected = true;

      const response = await fetch(config.url, {
        method: config.method || 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...config.headers,
        },
        body: config.body ? JSON.stringify(config.body) : undefined,
        signal,
      });

      if (!response.ok) {
        throw new SSEHttpError(response.status, response.statusText);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          onDone?.();
          isConnected = false;
          break;
        }

        // 디코딩된 청크를 버퍼에 추가
        buffer += decoder.decode(value, { stream: true });

        // SSE 메시지 파싱 (줄 단위로 처리)
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // 마지막 불완전한 줄은 버퍼에 유지

        let event: SSEEvent<TEventType, TData> | null = null;
        let rawData = '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataLine = line.slice(6); // 'data: ' 제거
            // 여러 줄의 data가 있을 수 있으므로 누적
            rawData += (rawData ? '\n' : '') + dataLine;
            if (!event) {
              event = {
                type: 'message' as TEventType,
                data: undefined as TData,
              };
            }
          } else if (line.startsWith('event: ')) {
            const eventType = line.slice(7).trim();
            if (event) {
              event.type = eventType as TEventType;
            }
          } else if (line.startsWith('id: ')) {
            const id = line.slice(4).trim();
            if (event) {
              event.id = id;
            }
          } else if (line === '') {
            // 빈 줄은 이벤트의 끝을 의미
            if (event && rawData) {
              try {
                // JSON 형태의 데이터를 파싱
                event.data = JSON.parse(rawData) as TData;
              } catch (parseError) {
                // JSON 파싱 실패 시 에러 처리
                const error = new Error(
                  `Failed to parse SSE data as JSON: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`,
                );
                onError?.(error);
                Sentry.captureException(error, {
                  extra: {
                    rawData: rawData.substring(0, 1000), // 최대 1000자만 기록
                  },
                });
                rawData = '';
                event = null;
                continue;
              }
              rawData = '';

              if (event.type === 'done') {
                onDone?.();
                isConnected = false;
                return;
              } else if (event.type === 'error') {
                const error = new Error(
                  typeof event.data === 'string' ? event.data : JSON.stringify(event.data),
                );
                onError?.(error);
                Sentry.captureException(error);
              } else {
                onMessage?.(event);
              }
              event = null;
            }
          }
        }
      }
    } catch (error) {
      isConnected = false;

      // AbortError는 정상적인 종료이므로 무시
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      const sseError = error instanceof Error ? error : new Error('Unknown SSE error');
      onError?.(sseError);
      Sentry.captureException(sseError, {
        extra: {
          url: config.url,
          method: config.method,
        },
      });
    }
  };

  // 비동기로 연결 시작
  connectSSE();

  return {
    abort: () => {
      abortController?.abort();
      isConnected = false;
    },
    isConnected: () => isConnected,
  };
}

/**
 * 401 발생 시 refresh token을 이용해 재시도하는 인증 SSE 연결 함수입니다.
 */
export function createAuthenticatedSSEConnection<TEventType = string, TData = unknown>(
  config: AuthenticatedSSERequestConfig,
  options: SSEOptions<TEventType, TData> = {},
): SSEController {
  const { maxAuthRetryAttempts = MAX_AUTH_RETRY_ATTEMPTS, ...requestConfig } = config;
  const originalOnError = options.onError;

  let retryAttempts = 0;
  let currentController: SSEController | null = null;
  let manuallyAborted = false;

  const connect = () => {
    if (manuallyAborted) {
      return;
    }

    const tokens = AuthService.getTokens();
    if (!tokens?.accessToken) {
      const noTokenError = new Error('No authentication tokens found');
      originalOnError?.(noTokenError);
      return;
    }

    currentController = createSSEConnection<TEventType, TData>(
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      },
      {
        ...options,
        onError: (error) => {
          if (!isSSEHttpError(error) || error.status !== 401 || manuallyAborted) {
            originalOnError?.(error);
            return;
          }

          const refreshToken = AuthService.getTokens()?.refreshToken;
          if (!refreshToken) {
            handleAuthFailure();
            originalOnError?.(error);
            return;
          }

          if (retryAttempts >= maxAuthRetryAttempts) {
            Sentry.captureException(new Error('Max token refresh retries exceeded for SSE'), {
              extra: {
                url: requestConfig.url,
                retries: retryAttempts,
              },
            });
            handleAuthFailure();
            originalOnError?.(error);
            return;
          }

          retryAttempts += 1;

          void refreshAccessToken()
            .then(() => {
              if (!manuallyAborted) {
                connect();
              }
            })
            .catch((refreshError) => {
              Sentry.captureException(refreshError, {
                extra: {
                  context: 'sse token refresh failed',
                  url: requestConfig.url,
                  retryAttempt: retryAttempts,
                },
              });
              handleAuthFailure();
              originalOnError?.(
                refreshError instanceof Error ? refreshError : new Error('Token refresh failed'),
              );
            });
        },
      },
    );
  };

  connect();

  return {
    abort: () => {
      manuallyAborted = true;
      currentController?.abort();
    },
    isConnected: () => currentController?.isConnected() ?? false,
  };
}
